import React from 'react';
import { Link } from 'react-router-dom';
import {
  Video,
  Target,
  Eye,
  Heart,
  Users,
  Zap,
  Quote,
  Mail,
  MapPin,
  Shield,
} from 'lucide-react';
import { FREE_MEETING_MAX_DURATION_MINUTES } from '../utils/plan.ts';

const values = [
  {
    icon: Target,
    title: 'Odak',
    description: 'Tek bir hedef: toplantılarınızı daha verimli ve keyifli hale getirmek.',
  },
  {
    icon: Zap,
    title: 'Hız',
    description: 'Hızlı bağlantı, hızlı destek ve sürekli iyileştirme.',
  },
  {
    icon: Shield,
    title: 'Güven',
    description: 'KVKK uyumlu, şeffaf ve güvenilir bir platform sunuyoruz.',
  },
  {
    icon: Heart,
    title: 'Müşteri Odaklılık',
    description: 'Kullanıcı geri bildirimleriyle sürekli gelişiyoruz.',
  },
];

const stats = [
  { value: `${FREE_MEETING_MAX_DURATION_MINUTES} dk`, label: 'Ücretsiz toplantı süresi' },
  { value: 'AES-256', label: 'Sohbet şifreleme' },
  { value: '1 tık', label: 'Anında toplantı başlat' },
  { value: '%100', label: 'Türkçe arayüz' },
];

const team = [
  { name: 'Ekibimiz', role: 'TBKMeet Ailesi', quote: 'Türkiye’den dünyaya açılan, güvenilir ve kaliteli bir video konferans deneyimi sunmak için çalışıyoruz.' },
];

function About() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-24 pb-20">
      {/* Hero */}
      <section className="px-6 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl -top-48 -left-48" />
          <div className="absolute w-96 h-96 bg-pink-500/20 rounded-full blur-3xl -bottom-48 -right-48" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Hakkımızda
          </h1>
          <p className="text-xl text-gray-400 mb-6">
            Toplantılarınızı bir üst seviyeye taşıyan, Türkiye merkezli video konferans platformu.
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <Video className="w-6 h-6 text-purple-400" />
            <span className="font-semibold text-white">TBKMeet</span>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 hover:bg-white/10 transition">
              <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Misyonumuz</h2>
              <p className="text-gray-400 leading-relaxed">
                Her büyüklükteki ekip ve bireyin, kristal netliğinde görüntü ve kesintisiz bağlantı ile
                bir araya gelmesini sağlamak. Türkiye’den dünyaya açılan, güvenilir ve kullanımı kolay
                bir video konferans deneyimi sunuyoruz.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 hover:bg-white/10 transition">
              <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Vizyonumuz</h2>
              <p className="text-gray-400 leading-relaxed">
                Türkiye ve bölgede tercih edilen video konferans platformu olmak. KVKK uyumu,
                Türkçe arayüz ve yerel destek ile iş dünyasının iletişim ihtiyacını karşılamak.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Değerlerimiz</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            TBKMeet’i inşa ederken rehberimiz olan ilkeler
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 hover:bg-white/10 transition text-center"
                >
                  <Icon className="w-10 h-10 text-purple-400 mx-auto mb-4" />
                  <h3 className="text-white font-semibold mb-2">{v.title}</h3>
                  <p className="text-gray-400 text-sm">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-2xl border border-white/10 p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">{s.value}</div>
                  <div className="text-gray-400 text-sm">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote / Team */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 md:p-10">
            <Quote className="w-10 h-10 text-purple-400/60 mb-4" />
            <p className="text-gray-300 text-lg italic mb-6">
              {team[0].quote}
            </p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-semibold text-white">{team[0].name}</div>
                <div className="text-gray-400 text-sm">{team[0].role}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Bizimle İletişime Geçin</h2>
          <p className="text-gray-400 mb-6">
            Sorularınız veya iş birliği talepleriniz için bize ulaşın.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-gray-300">
            <a
              href="mailto:info@tbkmeet.com"
              className="flex items-center gap-2 hover:text-purple-400 transition"
            >
              <Mail className="w-5 h-5" />
              info@tbkmeet.com
            </a>
            <span className="hidden sm:inline text-white/30">|</span>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-400" />
              Ankara, Türkiye
            </div>
          </div>
          <Link
            to="/contact"
            className="inline-flex mt-6 px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:shadow-lg hover:shadow-purple-500/30 transition"
          >
            İletişim sayfasına git
          </Link>
        </div>
      </section>
    </div>
  );
}

export default About;
